<template>
  <div class="p-company-settings-data-and-privacy" v-loading="loading">
    <h2>{{ $t('companySettings.data.title') }}</h2>
    <p>{{ $t('companySettings.data.pageIntroMessage') }}</p>
    <hr />
    <h3>{{ $t('companySettings.data.exportData') }}</h3>
    <VMessage
      type="warning"
      :fullwidth="true"
      v-if="!permissionCompany">
      <template v-slot:icon>
        <ExclamationTriangleIcon />
      </template>
      <template v-slot:text>
        {{ $t('companySettings.data.permissionWarning') }}
      </template>
    </VMessage>
    <template v-else>
      <VGrid>
        <div slot="gridItem" type="3">
          <h4>{{ $t('functional.envelopes') }}</h4>
          <p>{{ $t('companySettings.data.exportEnvelopesExplainer') }}</p>
          <p>
            <VButton
                type="cta-1"
                :disabled="envelopesExportTriggered"
                data-ec="export_data"
                data-et="open_envelope_export_modal_button"
                @click="openExportModal('envelopes')">
              <span>{{ $t('companySettings.data.exportType', { type: $t('functional.envelopes') }) }}</span>
            </VButton>
          </p>
          <p v-if="envelopesExportTriggered">{{ $t('companySettings.data.exportInProgress', { type: $t('functional.envelopes') }) }}</p>
        </div>
        <div slot="gridItem" type="3">
          <h4>{{ $t('functional.templates') }}</h4>
          <p>{{ $t('companySettings.data.exportTemplatesExplainer') }}</p>
          <p>
            <VButton
                type="cta-1"
                :disabled="templatesExportTriggered"
                data-ec="export_data"
                data-et="open_template_export_modal_button"
                @click="openExportModal('templates')">
              <span>{{ $t('companySettings.data.exportType', { type: $t('functional.templates') }) }}</span>
            </VButton>
          </p>
          <p v-if="templatesExportTriggered">{{ $t('companySettings.data.exportInProgress', { type: $t('functional.templates') }) }}</p>
        </div>
        <div slot="gridItem" type="3">
          <h4>{{ $t('functional.contacts') }}</h4>
          <p>{{ $t('companySettings.data.exportContactsExplainer') }}</p>
          <p>
            <VButton
                type="cta-1"
                :disabled="contactsExportTriggered"
                data-ec="export_data"
                data-et="open_contacts_export_modal_button"
                @click="openExportModal('contacts')">
              <span>{{ $t('companySettings.data.exportType', { type: $t('functional.contacts') }) }}</span>
            </VButton>
          </p>
          <p v-if="contactsExportTriggered">{{ $t('companySettings.data.exportInProgress', { type: $t('functional.contacts') }) }}</p>
        </div>
        <div slot="gridItem" type="3">
          <h4>{{ $t('functional.users') }}</h4>
          <p>{{ $t('companySettings.data.exportUsersExplainer') }}</p>
          <p>
            <VButton
                type="cta-1"
                :disabled="usersExportTriggered"
                data-ec="export_data"
                data-et="open_users_export_modal_button"
                @click="openExportModal('users')">
              <span>{{ $t('companySettings.data.exportType', { type: $t('functional.users') }) }}</span>
            </VButton>
          </p>
          <p v-if="usersExportTriggered">{{ $t('companySettings.data.exportInProgress', { type: $t('functional.users') }) }}</p>
        </div>
      </VGrid>
      <ExportModal :type="exportType" :show="exportModalVisible" @exportTriggered="setNewExportTrigger" @close="exportModalVisible = false;" />
    </template>
    <hr />
    <div>
      <h3>{{ $t('companySettings.data.ourPolicies') }}</h3>
      <VGrid>
        <div slot="gridItem" type="6">
          <ul>
            <li>
              <a
                  href="https://www.signable.co.uk/terms-conditions"
                  data-ec="export_data"
                  data-et="terms_of_service_link"
                  target="_blank">
                {{ $t('companySettings.data.termsOfService') }}
              </a>
            </li>
            <li>
              <a
                  href="https://www.signable.co.uk/privacy-policy"
                  data-ec="data"
                  data-et="privacy_policy_link"
                  target="_blank">
                {{ $t('companySettings.data.privacyPolicy') }}
              </a>
            </li>
            <li>
              <a
                  href="https://www.signable.co.uk/compliance-and-data-security"
                  data-ec="export_data"
                  data-et="compliance_&_security_link"
                  target="_blank">
                {{ $t('companySettings.data.complianceAndDataSecurity') }}
              </a>
            </li>
            <li>
              <a
                  :href="`${signingPageUrl}/#/widget/kcOSOPmxGT`"
                  data-ec="export_data"
                  data-et="data_processing_agreement_link"
                  target="_blank">
                {{ $t('companySettings.data.dataProcessingAgreement') }}
              </a>
            </li>
          </ul>
        </div>
        <div slot="gridItem" type="6">
          <p>{{ $t('companySettings.data.policiesHelpMessage') }}</p>
        </div>
      </VGrid>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import ExportModal from '@/views/modals/data/export.vue';
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'CompanySettingsData',
  components: {
    VButton,
    VMessage,
    VGrid,
    ExportModal,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      loading: true,
      exportModalVisible: false,
      exportType: '',
      newExportsTriggered: {
        users: false,
        contacts: false,
        envelopes: false,
        templates: false,
      },
      signingPageUrl: process.env.VUE_APP_SIGNING_PAGE,
    };
  },
  created() {
    this.$store.dispatch('company/GET').finally(() => { this.loading = false; });
  },
  computed: {
    ...mapGetters({
      company: 'company/company',
      userHasRole: 'roles/userHasRole',
    }),
    usersExportTriggered() {
      return this.company.export_users > 0 || this.newExportsTriggered.users;
    },
    contactsExportTriggered() {
      return this.company.export_contacts > 0 || this.newExportsTriggered.contacts;
    },
    templatesExportTriggered() {
      return this.company.export_templates > 0 || this.newExportsTriggered.templates;
    },
    envelopesExportTriggered() {
      return this.company.export_envelopes > 0 || this.newExportsTriggered.envelopes;
    },
    permissionCompany() {
      return this.userHasRole('super-admin');
    },
  },
  methods: {
    openExportModal(exportType) {
      this.exportType = exportType;
      this.exportModalVisible = true;
    },
    setNewExportTrigger(exportType) {
      this.newExportsTriggered[exportType] = true;
    },
  },
};
</script>

<style scoped>
.a-button {
  display: inline-block;
  float: none;
}
</style>
